import { Component, EventEmitter, Input, Output, type OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import {
  MessagingChannelType,
  availableMessageChannels,
  MessagingChannelsConfig,
  isEmailEnabledOnMessagingChannel,
  selectMessagingChannels,
  emailMessagingChannel,
  MessagingChannelOption,
  MessagingChannelsService,
  MessageEditorFeatures,
} from '@tokeet-frontend/message'
import {
  Destroyable,
  distinctUntilChangedByIds,
  isSomething,
  selectCurrentAccount,
  untilDestroy,
  Account,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { map, startWith, switchMap, tap } from 'rxjs/operators'
import { AppOverviewDialogService } from '@tokeet-frontend/billing'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { select, Store } from '@ngrx/store'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ConnectWhatsappDialogService } from '../../../connect-whatsapp-dialog/connect-whatsapp-dialog.service'
import { ConnectInstagramDialogService } from '@tv3/containers/messages/connect-instagram-dialog/connect-instagram-dialog.service'
import { ConnectFacebookDialogService } from '@tv3/containers/messages/connect-facebook-dialog/connect-facebook-dialog.service'
import { SendWhatsappTemplateDialogService } from '../../../send-whatsapp-template-dialog/send-whatsapp-template-dialog.service'
import { selectGuestById } from '@tv3/store/guest/guest.selectors'
import { Guest } from '@tv3/store/guest/guest.model'
import { ConnectGmailDialogService } from '@tv3/containers/messages/connect-gmail-dialog/connect-gmail-dialog.service'
import { environment } from '@tv3/environments/environment'
import { selectMessagesByInquiryId } from '@tv3/store/message/message.selectors'
import { Message } from '@tv3/store/message/message.model'
import * as lodash from 'lodash'
import { combineLatest } from 'rxjs'

@Component({
  selector: 'app-messaging-channel-switcher',
  templateUrl: './messaging-channel-switcher.component.html',
  styleUrls: ['./messaging-channel-switcher.component.scss'],
})
export class MessagingChannelSwitcherComponent extends Destroyable implements OnInit {
  @Input() form: FormGroup
  @Input() inquiry: Inquiry

  get channelCtrl() {
    return this.form?.get('channel')
  }

  get guestEmail() {
    return this.inquiry?.guest?.primaryEmail || this.inquiry?.guestDetails?.email
  }

  selectedChannel: MessagingChannelType
  selectedChannelOption: (typeof availableMessageChannels)[number]
  get isEmailSelected() {
    return this.selectedChannel === MessagingChannelType.Email
  }

  @Output() featuresChange = new EventEmitter<MessageEditorFeatures[]>()

  messagingChannelOptions = [emailMessagingChannel, ...availableMessageChannels]
  messagingChannels: MessagingChannelsConfig = {}

  MessagingChannelType = MessagingChannelType
  isWhatsappTemplatesOpen = false
  isMessagingSubscribed = false
  isWhatsappFreeMessageEnabled = false

  isProduction = environment.environment === 'production'

  account: Account
  guest: Guest
  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private toaster: Toaster,
    private msgChannelService: MessagingChannelsService,
    private appOverviewDialog: AppOverviewDialogService,
    private connectWhatsappDialog: ConnectWhatsappDialogService,
    private connectInstagramDialog: ConnectInstagramDialogService,
    private connectFacebookDialog: ConnectFacebookDialogService,
    private connectGmailDialog: ConnectGmailDialogService,
    private sendWhatsappTemplateDialog: SendWhatsappTemplateDialogService
  ) {
    super()
  }

  ngOnInit(): void {
    this.form.addControl('channel', this.fb.control(this.getChannelFromSource(this.inquiry.inquirySource)))

    this.store.pipe(select(selectCurrentAccount), untilDestroy(this)).subscribe((account) => {
      this.account = account
    })

    const guest$ = this.store.pipe(
      select(selectGuestById, { id: this.inquiry.guestId }),
      map((guest) => guest || this.inquiry.guest)
    )
    guest$.pipe(untilDestroy(this)).subscribe((guest) => {
      this.guest = guest
    })

    // re-check the convo-started gate whenever the inquiry's messages change,
    // so a guest reply unlocks WhatsApp without reloading the page
    const messages$ = this.store.pipe(
      select(selectMessagesByInquiryId(this.inquiry.id)),
      distinctUntilChangedByIds(),
      map((messages) => lodash.sortBy(messages, (m) => m.created).reverse())
    )

    combineLatest([messages$, guest$])
      .pipe(
        switchMap(([messages, guest]: [Message[], Guest]) =>
          this.msgChannelService
            .isWhatsappFreeMessageEnabled(guest.id)
            .pipe(map((isWhatsappFreeMessageEnabled) => ({ messages, isWhatsappFreeMessageEnabled })))
        ),
        untilDestroy(this)
      )
      .subscribe(({ messages, isWhatsappFreeMessageEnabled }) => {
        const messageChannel = this.getChannelFromSource(messages?.[0]?.sentWith || this.inquiry.inquirySource)
        this.isWhatsappFreeMessageEnabled = !!isWhatsappFreeMessageEnabled?.data?.hasSentMessageWithin24Hours
        if (!this.isWhatsappFreeMessageEnabled && messageChannel === MessagingChannelType.Whatsapp) {
          this.form.patchValue({ channel: MessagingChannelType.Email })
        } else {
          this.form.patchValue({ channel: messageChannel })
        }
      })

    this.store
      .pipe(select(selectActiveProductPlanView(ProductsForPlan.Messaging)), untilDestroy(this))
      .subscribe((sub) => {
        this.isMessagingSubscribed = !!sub?.isValid
      })

    this.store.pipe(select(selectMessagingChannels), untilDestroy(this)).subscribe((d) => {
      this.messagingChannels = d
    })

    this.channelCtrl.valueChanges
      .pipe(startWith(this.channelCtrl.value), untilDestroy(this))
      .subscribe((channel: MessagingChannelType) => {
        if (!isSomething(channel)) {
          this.channelCtrl.setValue(this.getChannelFromSource(this.inquiry.inquirySource))
          return
        }
        this.selectedChannel = channel
        this.selectedChannelOption = this.messagingChannelOptions.find((t) => t.id === channel) as any
        this.featuresChange.emit(this.getAvailableFeatures())
      })
  }

  getAvailableFeatures(): MessageEditorFeatures[] {
    const channel = this.selectedChannel
    const item = this.messagingChannelOptions.find((t) => t.id === channel)
    if (!item) return ['quote', 'template', 'attachment', 'ai']
    return item.availableMessageFeatures
  }

  onSubscribeMessaging() {
    this.appOverviewDialog.open(ProductsForPlan.Messaging)
  }

  onChangeChannel(config: MessagingChannelOption) {
    if (!config || !this.isMsgChannelEnabled(config)) {
      return
    }

    if (!config.isSubscriptionRequired && !config.oauth) {
      this.form.patchValue({ channel: config.id })
      this.validateSelectedChannel(config.id)
      return
    }

    const isChannelEnabled = this.messagingChannels?.[config.channel]?.enabled
    if (config.isSubscriptionRequired && !this.isMessagingSubscribed) {
      this.toaster.warning($localize`Please subscribe Messaging feature before you can use it.`)
      this.onSubscribeMessaging()
    } else if (config.oauth && !isChannelEnabled) {
      this.toaster.warning($localize`Please connect to ` + config.label + $localize` before you can use it.`)
      this.onConnect(config.id)
    } else {
      this.form.patchValue({ channel: config.id })
      this.validateSelectedChannel(config.id)
    }
  }

  getChannelDisabledTooltip(config: MessagingChannelOption) {
    switch (config.id) {
      case MessagingChannelType.Whatsapp:
        return $localize`This guest has not sent a message within the last 24 hours`
      case MessagingChannelType.Facebook:
        return $localize`This guest does not have a Facebook account`
      case MessagingChannelType.Instagram:
        return $localize`This guest does not have an Instagram account`
      case MessagingChannelType.SMS:
        return $localize`This guest does not have a phone number`
      case MessagingChannelType.Gmail:
        return $localize`This guest does not have an email address`
      case MessagingChannelType.Email:
        return this.inquiry?.id
          ? $localize`This guest does not have an email address`
          : $localize`Create an Inquiry first to be able to message this guest`
      default:
        return ''
    }
  }

  isMsgChannelAvailable = (config: MessagingChannelOption, guestEmail: string) => {
    if (config.id === MessagingChannelType.Email && !this.inquiry?.id) {
      return false
    }
    return isEmailEnabledOnMessagingChannel(guestEmail, config.id)
  }

  isMsgChannelEnabled(config: MessagingChannelOption) {
    if (!this.isMsgChannelAvailable(config, this.guestEmail)) {
      return false
    }
    if (config.isSubscriptionRequired && !this.isMessagingSubscribed) {
      return false
    }
    if (config.oauth && !this.messagingChannels?.[config.channel]?.enabled) {
      return false
    }
    if (config.id === MessagingChannelType.Whatsapp && !this.isWhatsappFreeMessageEnabled) {
      return false
    }
    return true
  }

  getChannelFromSource(source: string) {
    const option = this.messagingChannelOptions.find((t) => t.channel.startsWith(lodash.toLower(source)))
    return option && this.isMsgChannelAvailable(option, this.guestEmail) ? option.id : 'email'
  }

  onSendWhatsappTemplate() {
    this.sendWhatsappTemplateDialog.open(this.inquiry)
  }

  validateSelectedChannel(channel: MessagingChannelType) {
    return !!this.getContact(channel)
  }

  getContact(channel: MessagingChannelType): string {
    switch (channel) {
      case MessagingChannelType.Whatsapp:
        const whatsappPhone = this.guest?.whatsapp_phone || ''
        if (!whatsappPhone) {
          this.toaster.error($localize`This guest does not have a WhatsApp phone number`)
        }
        return whatsappPhone
      case MessagingChannelType.SMS:
        const phone = this.guest?.phone || ''
        if (!phone) {
          this.toaster.error($localize`This guest does not have a phone number`)
        }
        return phone
      default:
        const email = this.guestEmail || ''
        if (!email) {
          this.toaster.error($localize`This guest does not have an email address`)
        }
        return email
    }
  }

  onConnect(channel: MessagingChannelType) {
    switch (channel) {
      case MessagingChannelType.Instagram:
        this.connectInstagramDialog.open()
        break
      case MessagingChannelType.Whatsapp:
        this.connectWhatsappDialog.open(this.inquiry.guest, this.inquiry.id ? this.inquiry : null)
        break
      case MessagingChannelType.Facebook:
        this.connectFacebookDialog.open()
        break
      case MessagingChannelType.Gmail:
        this.connectGmailDialog.open()
        break
      default:
        break
    }
  }
}
